import { useMemo } from 'react';
import * as THREE from 'three';
import {
  abs,
  cameraPosition,
  color,
  float,
  fract,
  fwidth,
  max,
  min,
  mix,
  positionWorld,
  vec3,
} from 'three/tsl';
import { MeshBasicNodeMaterial, PointsNodeMaterial } from 'three/webgpu';

const GRID = { cell: 4, section: 20, fadeDistance: 260, fadeStrength: 2 };

// A line every `step` metres in world space, `thickness` in pixels, antialiased by fwidth.
function gridLine(step: number, thickness: number) {
  const c = positionWorld.xz.div(step);
  const d = abs(fract(c.sub(0.5)).sub(0.5)).div(fwidth(c).mul(thickness));
  return float(1).sub(min(min(d.x, d.y), float(1)));
}

function gridMaterial(): MeshBasicNodeMaterial {
  const m = new MeshBasicNodeMaterial();
  const cell = gridLine(GRID.cell, 0.4);
  const section = gridLine(GRID.section, 0.8);
  const dist = positionWorld.sub(cameraPosition).length();
  const fade = float(1).sub(min(dist.div(GRID.fadeDistance), float(1)).pow(GRID.fadeStrength));
  m.colorNode = mix(color('#1c2027'), color('#2a3140'), section);
  m.opacityNode = max(cell, section).mul(fade);
  m.transparent = true;
  m.depthWrite = false;
  m.side = THREE.DoubleSide;
  return m;
}

function starsGeometry(count: number, radius: number, depth: number): THREE.BufferGeometry {
  const positions = new Float32Array(count * 3);
  const v = new THREE.Vector3();
  for (let i = 0; i < count; i++) {
    v.randomDirection().multiplyScalar(radius + Math.random() * depth);
    v.toArray(positions, i * 3);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  return g;
}

function starsMaterial(): PointsNodeMaterial {
  const m = new PointsNodeMaterial();
  m.colorNode = vec3(0.8, 0.85, 0.92);
  m.size = 1.6;
  m.sizeAttenuation = false;
  m.transparent = true;
  m.opacity = 0.7;
  m.depthWrite = false;
  m.blending = THREE.AdditiveBlending;
  return m;
}

export function Void() {
  const grid = useMemo(() => gridMaterial(), []);
  const stars = useMemo(() => starsMaterial(), []);
  const starField = useMemo(() => starsGeometry(2500, 400, 80), []);
  return (
    <>
      <color attach="background" args={['#07080a']} />
      <fog attach="fog" args={['#07080a', 220, 700]} />
      <points geometry={starField} material={stars} />
      <mesh position={[0, -0.02, -60]} rotation-x={-Math.PI / 2} material={grid}>
        <planeGeometry args={[2000, 2000]} />
      </mesh>
    </>
  );
}
